import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { LISTING_STATE_CLOSED } from '../../util/types';
import { formatMoney } from '../../util/currency';
import config from '../../config';
import { ModalInMobile, Button, NamedLink } from '../../components';
import { BookingDatesForm } from '../../forms';

import css from './BookingPanel.css';

const MODAL_BREAKPOINT = 1023;

const priceData = (price, intl) => {
  if (price) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openBookModal = (isOwnListing, isClosed, setIsBook) => {
  if (isClosed) {
    window.scrollTo(0, 0);
  } else {
    setIsBook(true);
  }
};

const closeBookModal = (setIsBook) => {
  setIsBook(false);
};

const BookingPanel = props => {
  const {
    rootClassName,
    className,
    listing,
    isOwnListing = false,
    unitType = config.bookingUnitType,
    onSubmit,
    onAddToCart,
    title,
    subTitle = null,
    authorDisplayName,
    onManageDisableScrolling,
    timeSlots = null,
    fetchTimeSlotsError = null,
    intl,
    promo,
    addToCartSuccess
  } = props;

  const [isBook, setIsBook] = useState(false);

  const price = listing.attributes.price;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingDatesForm = hasListingState && !isClosed;
  const showClosedListingHelpText = listing.id && isClosed;
  const { formattedPrice, priceTitle } = priceData(price, intl);

  const subTitleText = !!subTitle
    ? subTitle
    : showClosedListingHelpText
      ? intl.formatMessage({ id: 'BookingPanel.subTitleClosedListing' })
      : null;

  const classes = classNames(rootClassName || css.root, className);

  const listingData = listing ? {
    name: listing.attributes.title,
    productId: listing.id.uuid,
    price: listing.attributes.price.amount / 100,
    category: listing.attributes.category,
    brand: listing.provider?.attributes?.name,
    provider: listing.provider?.attributes,
    businessCategory: listing.attributes.businessCategory
  } : {};

  useEffect(() => {
    addToCartSuccess && setIsBook(false);
  }, [addToCartSuccess])

  return (
    <div className={classes}>
      <ModalInMobile
        className={css.container}
        containerClassName={css.modalContainer}
        id="BookingDatesFormInModal"
        isModalOpenOnMobile={isBook}
        onClose={() => closeBookModal(setIsBook)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalHeading}>
          <h1 className={css.title}>{title}</h1>
          <div className={css.author}>
            <FormattedMessage id="BookingPanel.hostedBy" values={{ name: authorDisplayName }} />
          </div>
        </div>

        <div className={css.bookingHeading}>
          <div>
            {!isClosed && <><span className={css.price}>{formattedPrice}</span><span className={css.unit}> / <FormattedMessage id="ListingCard.perDay" /></span></>}
          </div>
          {(subTitleText && !isClosed) ? <div className={css.bookingHelp}>{subTitleText}</div> : null}
          {isClosed &&
            <div className={css.closedProductInfo}>
              <FormattedMessage id="BookingPanel.closedListingHelpText" />
              <NamedLink className={css.checkProduct} name="SearchPage"><FormattedMessage id="BookingPanel.checkOthers"/></NamedLink>
            </div>
          }
        </div>
        {showBookingDatesForm ? (
          <BookingDatesForm
            className={css.bookingForm}
            formId="BookingPanel"
            submitButtonWrapperClassName={css.bookingDatesSubmitButtonWrapper}
            unitType={unitType}
            onSubmit={onSubmit}
            onAddToCart={onAddToCart}
            price={price}
            isOwnListing={isOwnListing}
            bookingConditions={{ openingPattern: listing.provider?.attributes.openingPattern }}
            minBookingDays={listing.attributes.minBookingDays || listing.provider?.attributes.minBookingDays}
            timeSlots={timeSlots}
            fetchTimeSlotsError={fetchTimeSlotsError}
            listingData={listingData}
            promo={promo}
            listing={listing}
          />
        ) : null}
      </ModalInMobile>
      <div className={css.openBookingForm}>
        {
          isClosed ?
            <FormattedMessage id="BookingPanel.closedListingButtonText" /> :
            <div className={css.priceContainer}>
              <div className={css.header}><FormattedMessage id="BookingPanel.chooseDates" /></div>
              <div>
                <div className={css.priceValue} title={priceTitle}>
                  {formattedPrice} <span className={css.perUnit}><FormattedMessage id="BookingPanel.per1Day" /></span>
                </div>
              </div>
            </div>
        }
        {showBookingDatesForm ? (
          <>
            <Button
              id="BookButton"
              rootClassName={css.bookButton}
              onClick={() => openBookModal(isOwnListing, isClosed, setIsBook)}
            >
              <FormattedMessage id="BookingPanel.checkAvailabilty" />
            </Button>
          </>
        ) : isClosed ? (
          <div className={css.closedListingButton}>
            <NamedLink className={css.checkProduct} name="SearchPage"><FormattedMessage id="BookingPanel.checkOthers" /></NamedLink>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default compose(
  withRouter,
  injectIntl
)(BookingPanel);
